import { JSX, useState, useEffect, ReactElement, ReactNode, Fragment } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Button,
    IconButton,
    Stack,
    CircularProgress,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Chip,
    Paper,
    Alert,
    Snackbar,
    Collapse,
    Tooltip,
} from '@mui/material';
import { useNokTester } from "./hooks/nok-testing-testing-modal.hooks";
import GppBadIcon from '@mui/icons-material/GppBad';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useDataProvider } from 'react-admin';
import { NokTestingTestingModalProps } from "./types/nok-list-types";

type JsonPayload = Record<string, unknown> | unknown[];

// Results come back as free text whose vocabulary is set per Nok (simplePassLabel and friends),
// so this is a best-effort mapping for colour only — anything unrecognised stays neutral rather
// than being shown as a pass or a failure.
const POSITIVE_RESULTS: string[] = ['pass', 'passed', 'success', 'successful', 'yes', 'true', 'ok'];
const NEGATIVE_RESULTS: string[] = ['fail', 'failed', 'failure', 'no', 'false', 'catastrophe', 'critical'];

const resultChipColor: (value: unknown) => 'success'|'error'|'default' = (value: unknown): 'success'|'error'|'default' => {
    const normalised: string = String(value ?? '').trim().toLowerCase();

    if (POSITIVE_RESULTS.includes(normalised)) {
        return 'success';
    }
    if (NEGATIVE_RESULTS.includes(normalised)) {
        return 'error';
    }
    return 'default';
}

// Uploaded names are whatever the user's mail client produced and regularly run past a hundred
// characters, which would push the close button off the title row. The full name stays in the
// tooltip, so nothing is lost by trimming the visible label.
const FILE_NAME_MAX_LENGTH: number = 30;

const truncateFileName: (fileName: string) => string = (fileName: string): string => (
    fileName.length > FILE_NAME_MAX_LENGTH
        ? fileName.slice(0, FILE_NAME_MAX_LENGTH - 1).trimEnd() + '…'
        : fileName
);

const ResultChip: (props: { value: unknown }) => ReactElement = (props: { value: unknown }): ReactElement => {
    const label: string = String(props?.value ?? '—');

    return (
        <Chip label={ label }
              size="small"
              color={ resultChipColor(props?.value) }
              variant={ resultChipColor(props?.value) === 'default' ? 'outlined' : 'filled' }
              sx={ { textTransform: 'capitalize' } }
        />
    );
}

// Shared shell so both results panels and both email previews line up and scroll identically.
const Panel: (props: { title: string, children: ReactNode }) => ReactElement = (props: { title: string, children: ReactNode }): ReactElement => (
    <Box sx={ { flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' } }>
        <Typography variant="subtitle2" sx={ { mb: 1 } }>{ props.title }</Typography>
        <Paper variant="outlined"
               sx={ { flex: 1, minHeight: 0, overflow: 'auto', borderRadius: 2 } }
        >
            { props.children }
        </Paper>
    </Box>
);

const EmptyPanelMessage: (props: { message: string }) => ReactElement = (props: { message: string }): ReactElement => (
    <Box sx={ { p: 2 } }>
        <Typography variant="body2" color="text.secondary">{ props.message }</Typography>
    </Box>
);

type ResultLabelProps = {
    label: string,
    reason?: string,
    labelVariant?: 'body2'|'subtitle2',
    capitalize?: boolean,
};

// Name plus, when there is a reason to show, an inline toggle beside it. Reasons stay hidden
// until asked for — they run to several paragraphs and would otherwise dominate the table.
const ResultLabel: (props: ResultLabelProps) => ReactElement = (props: ResultLabelProps): ReactElement => {
    const [expanded, setExpanded] = useState<boolean>(false);
    const reason: string = props?.reason ?? '';
    const hasReason: boolean = reason.trim().length > 0;

    return (
        <>
            <Box sx={ { display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' } }>
                <Typography variant={ props?.labelVariant ?? 'body2' }
                            sx={ props?.capitalize ? { textTransform: 'capitalize' } : undefined }
                >
                    { props?.label }
                </Typography>
                { hasReason &&
                    <Button size="small"
                            variant="text"
                            onClick={ (): void => setExpanded(!expanded) }
                            aria-expanded={ expanded }
                            aria-label={ `${ expanded ? 'Hide' : 'Show' } reason for ${props?.label}` }
                            endIcon={ <ExpandMoreIcon fontSize="small"
                                                      sx={ {
                                                          transform: expanded ? 'rotate(180deg)' : 'none',
                                                          transition: 'transform 0.2s',
                                                      } }
                            /> }
                            sx={ { px: 0.5, py: 0, minWidth: 0, textTransform: 'none' } }
                    >
                        { expanded ? 'Hide reason' : 'Reason' }
                    </Button>
                }
            </Box>
            { hasReason &&
                <Collapse in={ expanded } unmountOnExit>
                    <Typography variant="caption"
                                color="text.secondary"
                                sx={ { display: 'block', whiteSpace: 'pre-wrap', mt: 0.5 } }
                    >
                        { reason }
                    </Typography>
                </Collapse>
            }
        </>
    );
}

export const NokTestingTestingModal: ({ nokTestingData, resetNokTestingData, outboundFileName, inboundFileName, form }: NokTestingTestingModalProps) => JSX.Element = ({ nokTestingData, resetNokTestingData, outboundFileName, inboundFileName, form  }: NokTestingTestingModalProps): JSX.Element => {
    const [openModal, setOpenModal] = useState<boolean>(false);
    const dataProvider = useDataProvider();
    const { isTestRunning, beginTest, testErrored, testResults, disconnectOperator } = useNokTester();
    const [generatingDownload, setGeneratingDownload] = useState<boolean>(false);
    const [exportError, setExportError] = useState<string|undefined>(undefined);

    function handleModalClose(): void {
        if (!isTestRunning && !generatingDownload) {
            setOpenModal(false);
            resetNokTestingData();
        }
    }

    // Escape hatch for a test that never returns: without this the modal cannot be closed
    // while isTestRunning, so a hung Operator could only be escaped by reloading the page.
    function handleCancelTest(): void {
        disconnectOperator();
        setOpenModal(false);
        resetNokTestingData();
    }

    async function loadAndBeginTest(): Promise<void> {
        await beginTest(dataProvider, nokTestingData);
    }

    function downloadJsonFile(): void {
        setGeneratingDownload(true);
        const now: Date = new Date();
        const date: string = `${now.getFullYear()}_${now.getMonth() + 1}_${now.getDate()}_${now.getHours()}_${now.getMinutes()}_${now.getSeconds()}`;
        let payload: JsonPayload = {
            'date' : date,
            'outbound_email_file' : outboundFileName,
            'inbound_email_file' : inboundFileName,
            'simple' : {},
            'complex' : [],
        };
        if (testResults?.simple?.length > 0) {
            const simpleResults: any[] = testResults.simple;
            for (const result of simpleResults) {
                payload['simple'][result.nokName] = result.result;
            }
        }
        if (testResults?.complex?.length > 0) {
            let complex = [];
            const complexResults: any[] = testResults.complex;
            for (const result of complexResults) {
                const res = result.result;
                let individualResults = [];
                for (const nokResult of res.results) {
                    individualResults.push({
                        'name' : nokResult.name,
                        'score' : nokResult.result,
                    });
                }
                complex.push({
                    'name' : result.nokName,
                    'final_score' : res.final_score,
                    'scores' : individualResults,
                });
            }
            payload['complex'] = complex;
        }
        try{
            const inputParts = outboundFileName.substring(0, 20);
            const fileName: string = date + "_" + inputParts.replaceAll(' ', "_");
            const json: string = JSON.stringify(payload, null, 2);

            const blob = new Blob([json], {
                type: "application/json;charset=utf-8",
            });

            const url: string = URL.createObjectURL(blob);

            const link: HTMLAnchorElement = document.createElement("a");
            link.href = url;
            link.download = fileName.endsWith(".json") ? fileName : `${fileName}.json`;

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error: any) {
            // Previously swallowed entirely, so a failed export looked identical to a
            // successful one from the user's side.
            console.error('Results export failed', error);
            setExportError('Could not export the results file.');
        }

        setGeneratingDownload(false);
    }

    useEffect( (): () => void => {
        if (nokTestingData !== undefined) {
            setOpenModal(true);
            loadAndBeginTest();
        }
        if (nokTestingData === undefined && openModal) {
            setOpenModal(false);
        }
        const handleBeforeUnload: (e: any) => void = (e: any): void => {
            disconnectOperator();
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return (): void => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    },[nokTestingData] );

    return (
        <>
        <Dialog open={ openModal }
                fullWidth
                maxWidth="lg"
                onClose={ handleModalClose }
                slotProps={ { paper: { sx: { height: '90vh', maxHeight: '90vh' } } } }
        >
            <DialogTitle sx={ {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                pr: 1,
            } }>
                <Box sx={ { display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 } }>
                    <span>{ `Nok Tester` }</span>
                    { (outboundFileName?.length > 0) &&
                        <Tooltip title={ `Enquiry email: ${outboundFileName}` }>
                            <Chip label={ truncateFileName(outboundFileName) }
                                  size="small"
                                  variant="outlined"
                            />
                        </Tooltip>
                    }
                </Box>
                <IconButton onClick={ handleModalClose }
                            disabled={ isTestRunning }
                            aria-label="Close"
                            size="small"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers
                           sx={ { display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 } }
            >
                { (isTestRunning) &&
                    <Stack alignItems="center" justifyContent="center" spacing={ 2 } sx={ { flex: 1 } }>
                        <CircularProgress />
                        <Typography variant="body1">{ "Running your test\u2026" }</Typography>
                        { (nokTestingData?.noks?.length > 0) &&
                            <Typography variant="body2" color="text.secondary">
                                { `${nokTestingData.noks.length} Nok${nokTestingData.noks.length === 1 ? '' : 's'} in this run` }
                            </Typography>
                        }
                    </Stack>
                }

                { (!isTestRunning && testErrored !== undefined && testResults === undefined) &&
                    <Stack alignItems="center" justifyContent="center" spacing={ 2 } sx={ { flex: 1 } }>
                        <GppBadIcon color="error" sx={ { fontSize: 72 } }/>
                        <Typography variant="body1" color="error">{ testErrored }</Typography>
                    </Stack>
                }

                { (!isTestRunning && testResults !== undefined) &&
                    <>
                        <Box sx={ {
                            display: 'flex',
                            gap: 2,
                            flex: '1 1 60%',
                            minHeight: 0,
                            flexDirection: { xs: 'column', md: 'row' },
                        } }>
                            <Panel title="Simple Nok Results">
                                { (testResults?.simple?.length > 0) ? (
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>{ "Nok" }</TableCell>
                                                <TableCell align="right">{ "Result" }</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            { testResults.simple.map( (item: any, index: number): ReactElement => (
                                                <TableRow key={ `nok_result_simple_${index}` }>
                                                    <TableCell>
                                                        <ResultLabel label={ item?.nokName }
                                                                     reason={ item?.reason }
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right" sx={ { verticalAlign: 'top' } }>
                                                        <ResultChip value={ item?.result } />
                                                    </TableCell>
                                                </TableRow>
                                            ) ) }
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <EmptyPanelMessage message="No simple Noks in this run." />
                                ) }
                            </Panel>

                            <Panel title="Complex Nok Results">
                                { (testResults?.complex?.length > 0) ? (
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>{ "Nok" }</TableCell>
                                                <TableCell align="right">{ "Result" }</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            { testResults.complex.map( (item: any, index: number): ReactElement => (
                                                <Fragment key={ `nok_result_complex_${index}` }>
                                                    <TableRow>
                                                        <TableCell>
                                                            <ResultLabel label={ item?.nokName }
                                                                         reason={ item?.reason }
                                                                         labelVariant="subtitle2"
                                                            />
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Chip label={ `Final: ${item?.result?.final_score ?? '\u2014'}` }
                                                                  size="small"
                                                                  color="primary"
                                                                  variant="outlined"
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                    { item?.result?.results?.map( (result: any, resultIndex: number): ReactElement => (
                                                        <TableRow key={ `nok_result_detail_${index}_${resultIndex}` }>
                                                            <TableCell sx={ { pl: 3, border: 0 } }>
                                                                <ResultLabel label={ result?.name?.replaceAll('_', ' ') }
                                                                             reason={ result?.reason }
                                                                             capitalize
                                                                />
                                                            </TableCell>
                                                            <TableCell align="right" sx={ { verticalAlign: 'top', border: 0 } }>
                                                                <ResultChip value={ result?.result } />
                                                            </TableCell>
                                                        </TableRow>
                                                    ) ) }
                                                </Fragment>
                                            ) ) }
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <EmptyPanelMessage message="No complex Noks in this run." />
                                ) }
                            </Panel>
                        </Box>

                        <Box sx={ {
                            display: 'flex',
                            gap: 2,
                            flex: '1 1 40%',
                            minHeight: 0,
                            flexDirection: { xs: 'column', md: 'row' },
                        } }>
                            <Panel title="Enquiry Email">
                                <Typography variant="body2"
                                            sx={ { p: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word' } }
                                >
                                    { form?.getValues('testEnquiry')?.[0]?.['body_plain'] || 'No enquiry body.' }
                                </Typography>
                            </Panel>
                            <Panel title="Response Email">
                                <Typography variant="body2"
                                            sx={ { p: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word' } }
                                >
                                    { form?.getValues('testResponse')?.[0]?.['body_plain'] || 'No response body.' }
                                </Typography>
                            </Panel>
                        </Box>
                    </>
                }
            </DialogContent>

            <DialogActions sx={ { justifyContent: 'space-between', px: 3, py: 2 } }>
                <Box>
                    { (testResults?.complex?.length > 0 || testResults?.simple?.length > 0) &&
                        <Button type="button"
                                variant="contained"
                                color="success"
                                disabled={ isTestRunning || generatingDownload }
                                onClick={ downloadJsonFile }
                        >
                            <span>{ "Export Results" }</span>
                        </Button>
                    }
                </Box>
                { isTestRunning ? (
                    <Button type="button"
                            variant="contained"
                            color="error"
                            onClick={ handleCancelTest }>
                        <span>{ "Cancel test" }</span>
                    </Button>
                ) : (
                    <Button type="button"
                            variant="contained"
                            color="secondary"
                            disabled={ generatingDownload }
                            onClick={ handleModalClose }>
                        <span>{ "Close" }</span>
                    </Button>
                ) }
            </DialogActions>
        </Dialog>

        <Snackbar open={ exportError !== undefined }
                  autoHideDuration={ 6000 }
                  onClose={ (): void => setExportError(undefined) }
                  anchorOrigin={ { vertical: 'bottom', horizontal: 'center' } }
        >
            <Alert severity="error" variant="filled" onClose={ (): void => setExportError(undefined) }>
                { exportError }
            </Alert>
        </Snackbar>
        </>
    );
}
